import { Box, Button, CircularProgress, ClickAwayListener, Icon, IconButton, MenuItem, MenuList, Paper, Popper, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useAdmissionExamStore } from '../../store/admission-exam.store'
import QuestionsPreviewDialog from '../../components/common/QuestionsPreviewDialog'
import { validateQuestionsImportFormatForQuestionBank } from '../../utils/exam-utilities'
import { Icons } from '../../utils/utilities'
import { toast } from 'react-toastify'
import api from '../../service/api'
import SectionBlock from '../../components/admission-exam-components/SectionBlock'
import QuestionBlock from '../../components/admission-exam-components/QuestionBlock'
import { useUserStore } from '../../store/user.store'
import BreadcrumbList from '../../components/common/BreadcrumbList'
import { observer } from 'mobx-react'
import ConfirmDialog from '../../components/ConfirmDialog'
import TryAgainMessageBlock from '../../components/common/TryAgainMessageBlock'
import DataLoadingSpinner from '../../components/common/DataLoadingSpinner'
import warningImg from '../../assets/warning.svg'
import CutoffEditDialog from '../../components/admission-exam-components/CutoffEditDialog'
import { Lock, LockOpen } from '@mui/icons-material'
import AikenParser from '../../service/AikenParser'
import CustomChip from '../../components/common/CustomChip'
import { by639_1 } from 'iso-language-codes'

const AdmissionExam = observer( () => {

    const { id } = useParams()

    const [editCutoffDialogState, setEditCutoffDialogState] = useState( false )
    const [languageOptionsOpen, setLanguageOptionsOpen] = useState( false )
    const [confirmDialogState, setConfirmDialogState] = useState( false )
    const [questionsPreview, setQuestionsPreview] = useState( false )
    const [hasPermission, setHasPermission] = useState( true )
    const [closingExam, setClosingExam] = useState( false )
    const [publishing, setPublishing] = useState( false )
    const [deleting, setDeleting] = useState( false )
    const [loading, setLoading] = useState( true )
    const [viewing, setViewing] = useState( null )
    const [error, setError] = useState( null )

    const { palette, border, shadows } = useTheme()
    const ExamStore = useAdmissionExamStore()
    const UserStore = useUserStore()

    const languageOptionsButtonRef = useRef()

    const navigate = useNavigate()

    // Observables
    const questionSections = ExamStore.getExamQuestionSections || []
    const questionFiles = ExamStore.getExamQuestionFiles || []
    const questions = ExamStore.getExamQuestions || []


    const extractQuestionFromFile = ( file, section ) => {
        return new Promise( ( resolve, reject ) => {
            const fileReader = new FileReader()
            fileReader.onload = function ( e ) {

                const aikenParserResult = AikenParser.validateImport( fileReader.result, "txt", null, section )
                const { questions } = aikenParserResult

                resolve( questions )
            }
            fileReader.onerror = function ( e ) {
                reject( new Error( "Error reading file" ) )
            }
            fileReader.readAsText( file )
        } )
    }

    const onImportChange = ( file ) => {
        if ( file.name.includes( ".txt" ) ) {
            setQuestionsPreview( { status: "loading" } )
            const fileReader = new FileReader()
            fileReader.onload = () => {
                // const { questions, errors, preview, errorLines } = validateQuestionsImportFormatForQuestionBank( fileReader.result, "txt", undefined, undefined )
                const aikenParserResult = AikenParser.validateImport( fileReader.result, "txt", undefined, undefined )

                const { questions, errors, raw, errorLines } = aikenParserResult
                setQuestionsPreview( { status: "loaded", data: raw, file: file, type: "txt", plainData: fileReader.result, errors, questions, errorLines } )
            }
            fileReader.readAsText( file )
        }
    }

    const viewQuestionFile = async ( fileId, name ) => {
        setViewing( fileId )
        try {
            const { data } = await api.admissionExams.getQuestionFile( id, fileId, name?.endsWith( "txt" ) ? 'blob' : undefined )
            const txtFile = new File( [data], name, { type: "application/octet-stream" } )
            if ( name?.endsWith( "txt" ) ) {
                onImportChange( txtFile )
            } else {
                const { questions, errors, raw, errorLines } = AikenParser.validateImport( data.file, "docx", undefined, undefined )
                setQuestionsPreview( { status: "loaded", data: raw, type: "doc", errors, questions, errorLines } )
            }
        } catch ( err ) {
            console.log( err )
        } finally {
            setViewing( null )
        }
    }

    const deleteExam = async () => {
        setDeleting( true )
        try {
            await api.admissionExams.deleteExam( id )
            navigate( '/admission-exam' )
        } catch ( err ) {
            toast( err?.response?.data?.message || err?.response?.data || "Something went wrong while deleting the exam" )
        }
        setDeleting( false )
        setConfirmDialogState( false )
    }

    const extractQuestionAndApprove = async ( files ) => {
        try {
            setPublishing( true )
            let allQuestions = []
            for ( const fileDetails of files ) {
                const { data } = await api.admissionExams.getQuestionFile( id, fileDetails.id, fileDetails.name?.endsWith( "txt" ) ? 'blob' : undefined )
                const file = new File( [data], fileDetails?.name, { type: "application/octet-stream" } )
                if ( fileDetails?.name?.endsWith( "txt" ) ) {

                    const questions = await extractQuestionFromFile( file, fileDetails.section )
                    allQuestions.push( ...questions )

                } else {

                    const aikenParserResult = AikenParser.validateImport( data.file, "docx", undefined, fileDetails.section )
                    const { questions } = aikenParserResult

                    allQuestions.push( ...questions )
                }
            }

            await api.admissionExams.approveExam( id, { questions: allQuestions, exam_id: id } )
            ExamStore.fetchExam( id )
            ExamStore.fetchExamQuestions( id )
            ExamStore.fetchExamLanguages( id )
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data || err?.response?.data?.message || "Something went wrong while publishing the exam!", { containerId: "main" } )
        } finally {
            setPublishing( false )
        }
    }

    const toggleExaminationClosure = async () => {
        setClosingExam( true )
        try {
            await api.admissionExams.toggleClose( id, { closed: !ExamStore.getExam?.exam?.closed } )
            const { success, error } = await ExamStore.fetchExam( id )
            if ( success ) {
                setError( null )
            } else {
                setError( error )
            }
        } catch ( err ) {
            toast( err?.response?.data?.message || "Something went wrong while " )
        } finally {
            setClosingExam( false )
        }
    }

    const getExam = useCallback( async () => {
        setLoading( true )
        await ExamStore.fetchExam( id )
        ExamStore.fetchExamQuestions( id )
        ExamStore.fetchExamLanguages( id )
        // if ( success ) {
        //     setError( null )
        // } else {
        //     setError( error )
        // }
        setLoading( false )
    }, [ExamStore, id] )


    useEffect( () => {
        if ( UserStore.getUser.user_role === 'subadmin' && !UserStore.getUser.user_permissions.exams ) {
            setHasPermission( false )
        }
    }, [UserStore] )

    useEffect( () => {
        getExam()
    }, [getExam] )

    return (
        <Box
            padding={{ xs: "10px", md: "20px" }}
            flexGrow={1}
            overflow="auto"
        >

            {hasPermission && <BreadcrumbList items={[
                { label: "Exams", link: "/admission-exam" },
                !loading && ExamStore?.getExam?.exam ? ExamStore.getExam?.exam?.exam_name : <CircularProgress size={12} />
            ]} />}

            <ConfirmDialog status={confirmDialogState} actionName="Delete" cancelAction={() => setConfirmDialogState( false )} waiting={deleting} confirmAction={deleteExam} message={<p>All the question and participation data will be deleted!</p>} />
            {editCutoffDialogState && <CutoffEditDialog currentExamCutoffs={ExamStore.getExam?.exam?.cutoff_details || { totalCutoff: 0 }} state={editCutoffDialogState} setState={setEditCutoffDialogState} sections={questionSections} />}

            <Popper placement='bottom-end' open={languageOptionsOpen} anchorEl={languageOptionsButtonRef.current} >
                <ClickAwayListener onClickAway={() => setLanguageOptionsOpen( false )}>
                    <Box border={border[1]} marginTop="5px" borderRadius="5px" overflow="clip" bgcolor={palette.background.paper}>
                        <Paper>
                            <MenuList dense>
                                {ExamStore.getTranslationDetails?.translationLanguges.map( lang => {
                                    const languageDetails = by639_1[lang.toLowerCase()]
                                    return (
                                        <MenuItem dense onClick={() => ExamStore.setExamQuestionsLanguage( lang )} selected={lang === ExamStore.getTranslationDetails?.selectedLanguage} key={lang}>
                                            <Typography fontSize="14px" component="span" color={lang === ExamStore.getTranslationDetails?.selectedLanguage ? palette.primary.contrastText : palette.common.black}>{lang.toUpperCase()} {languageDetails?.name}</Typography>
                                        </MenuItem>
                                    )
                                } )}
                            </MenuList>
                        </Paper>
                    </Box>
                </ClickAwayListener>
            </Popper>

            {!loading && hasPermission && ExamStore?.getExam?.exam && ExamStore.getExam?.exam?.exam_status === 'Draft' && <Box color="error.main" display="flex" gap="10px" alignItems="center" marginBottom="20px" padding="10px 20px" bgcolor={palette.error.light + "22"} border={`2px solid ${palette.error.main}`} borderRadius="10px" >
                <Icon color='inherit'>{Icons.default.Warning}</Icon>
                <Typography color="error.main" variant='subtitle2'>{UserStore?.getUser?.user_role?.toUpperCase() !== 'ADMIN' ? "This exam is not approved yet by the admin" : "This exam is yet to be approved! approve now to let students take this exam!"}</Typography>
            </Box>}

            {hasPermission ? <Paper sx={{ overflow: "hidden" }}>
                <QuestionsPreviewDialog readOnly setIsImporting={() => { }} getQuestions={() => { }} setQuestionsPreview={setQuestionsPreview} questionsPreview={questionsPreview} />

                {!loading && !error && <Box>
                    <Box padding="20px" sx={{ background: "linear-gradient(322.12deg, #F3C400 -21.29%, #E25A26 104.22%)" }} display="flex" justifyContent="space-between" gap="20px" flexWrap="wrap" alignItems="center">
                        <Box>
                            <Typography color="white" variant='h6'><em>{ExamStore.getExam?.exam?.exam_name}</em></Typography>
                            {+ExamStore.getExam.exam?.cutoff_details?.totalCutoff > 0 && <Typography color="white" variant='subtitle2'> <strong> <em>Cut-off %: </em> </strong> {+ExamStore.getExam.exam?.cutoff_details?.totalCutoff}</Typography>}
                            <Typography color="white" variant='body2'> <strong> <em>Exam Duration: </em> </strong> {+ExamStore.getExam.exam?.exam_duration?.split( ":" )[0]} Hours {+ExamStore.getExam.exam?.exam_duration?.split( ":" )[1]} Minutes</Typography>
                        </Box>
                        {!loading && !error && <Box display="flex" gap="20px" alignItems="center" justifyContent="space-between" flexWrap="wrap">
                            <Box display="flex" gap="10px" flexWrap="wrap" alignItems="center">
                                {ExamStore.getExam?.exam?.exam_status === 'Draft' && UserStore.getUser?.user_role?.toLowerCase() === "admin" && <Button color='errorMessage' onClick={() => setConfirmDialogState( true )} startIcon={Icons.default.DeleteForever} sx={{ textTransform: "capitalize" }} variant='contained' disableElevation >Unapprove and Delete</Button>}
                                {ExamStore.getExam?.exam?.exam_status === 'Draft' && UserStore.getUser?.user_role?.toLowerCase() === "admin" && <Button onClick={() => extractQuestionAndApprove( questionFiles )} disabled={publishing} startIcon={publishing && <CircularProgress size={14} />} color='success' sx={{ textTransform: "capitalize" }} variant='contained' disableElevation >Approve and publish</Button>}
                                {ExamStore.getExam?.exam?.exam_status === 'Published' && <Button color='success' disableFocusRipple disabled disableRipple disableTouchRipple startIcon={Icons.default.CheckCircleIcon} sx={{ textTransform: "capitalize" }} variant='contained' disableElevation >Published</Button>}
                                {ExamStore.getExam?.exam?.exam_status === 'Published' && <Button color='success' startIcon={Icons.default.EditIcon} sx={{ textTransform: "capitalize" }} onClick={() => setEditCutoffDialogState( true )} variant='contained' disableElevation >Edit Cut-off</Button>}
                                {ExamStore.getExam?.exam?.exam_status === 'Published' && <Button color='errorMessage' onClick={() => setConfirmDialogState( true )} startIcon={Icons.default.DeleteForever} sx={{ textTransform: "capitalize" }} variant='contained' disableElevation >Delete</Button>}
                                {ExamStore.getExam?.exam?.exam_status === 'Published' && <Button disabled={closingExam} color={ExamStore.getExam?.exam?.closed ? "warning" : 'errorMessage'} onClick={toggleExaminationClosure} startIcon={closingExam ? <CircularProgress size={14} /> : ExamStore.getExam?.exam?.closed ? <LockOpen /> : <Lock />} sx={{ textTransform: "capitalize" }} variant='contained' disableElevation >{ExamStore.getExam?.exam?.closed ? closingExam ? "opening exam.." : "Open Exam" : closingExam ? "Closing exam" : "Close Exam"}</Button>}
                                {ExamStore.getExam?.exam?.exam_status !== 'Draft' && <Link className='default-link' to={`/admission-exam/${id}/participation`}> <Button color='secondary' startIcon={Icons.default.AnalyticsIcon} sx={{ textTransform: "capitalize" }} variant='contained' disableElevation >View Participants</Button></Link>}
                            </Box>
                        </Box>}
                    </Box>

                    <Box padding="20px" display="flex" flexDirection="column" gap="20px">
                        <Box display="flex" gap="10px" alignItems="center" flexWrap="wrap" justifyContent="space-between" >
                            <Typography flexGrow={1} variant='h6' fontSize="16px" color="secondary">Questions</Typography>
                            {questions.length > 0 && ExamStore.getTranslationDetails?.translationsAvailable && <Button endIcon={Icons.default.KeyboardArrowDownIcon} onClick={() => setLanguageOptionsOpen( true )} ref={languageOptionsButtonRef} variant='contained' size='small' disableElevation sx={{ borderRadius: "10px", border: `1px solid ${palette.text.secondary}`, background: "none", color: palette.text.primary }} startIcon={Icons.default.Language}>
                                Question language: {ExamStore.getTranslationDetails?.selectedLanguage?.toUpperCase()}
                            </Button>}
                        </Box>

                        {questionSections.length > 1 && <Box display="flex" flexDirection="column" gap="10px">
                            {questionSections.map( ( section, index ) => (
                                <SectionBlock viewQuestionFile={viewQuestionFile} readOnly files={questionFiles.filter( q => q?.section === section )} index={index} section={section} questions={questions.filter( q => q?.question_section === section )} key={index} />
                            ) )}
                        </Box>}
                        {questionSections.length < 2 && ExamStore.getExam?.exam?.exam_status === 'Draft' && questionFiles.length > 0 && <Box display="flex" gap="10px" >
                            {questionFiles.map( ( fileDetails, index ) => {
                                return <Box border={border[1]} boxShadow={shadows[1]} display="flex" gap="10px" alignItems="center" borderRadius="10px" padding="10px" key={index}>
                                    <Typography variant='subtitle2'>{fileDetails.name}</Typography>
                                    <IconButton size='small' disabled={viewing === index} sx={{ padding: "2px" }} onClick={() => viewQuestionFile( fileDetails.id, fileDetails.name )} >{viewing === index ? <CircularProgress size={14} /> : Icons.default.OpenInNewIcon}</IconButton>
                                </Box>
                            } )}
                        </Box>}
                        {questionSections.length < 2 && questions.length > 0 && <Box display="flex" flexDirection="column" gap="10px" >
                            {questions.map( ( question, index ) => {
                                return <QuestionBlock readOnly defaultCollapseState={false} question={question} index={index} key={index} />
                            } )}
                        </Box>}
                    </Box>
                </Box>}
                {loading && !error && <DataLoadingSpinner waitingMessage="Loading Exam please wait..." />}
                {error && <TryAgainMessageBlock err={error?.message} code={error?.code} getData={getExam} loading={loading} />}
            </Paper> : <Paper sx={{ display: "flex", justifyContent: "center", padding: "20px", gap: "20px", alignItems: "center" }}>
                <img src={warningImg} width="40px" alt="No permission" />
                <Typography variant='subtitle2' color="textSecondary">You doesn't have permission to access this page!</Typography>
            </Paper>}

        </Box >
    )
} )

export default AdmissionExam
